import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { exportRegister } from './register-export.js';
import {
  ApiError,
  DOCUMENT_CATEGORIES,
  addDocumentVersion,
  createFolder,
  deleteDocument,
  deleteFolder,
  documentFileUrl,
  getDocument,
  isViewableInline,
  listDocuments,
  listFolders,
  moveDocumentToFolder,
  updateDocument,
  updateFolder,
  uploadDocument,
  type DocumentDetail,
  type DocumentRow,
  type Folder,
} from '../api.js';
import { formatDate } from '../labels.js';
import {
  Drawer,
  RegisterToolbar,
  RowActions,
  matches,
  useRegisterView,
  type RowAction,
} from './register-kit.js';
import { LOAD_ALL_LIMIT } from '../api/paging.js';

/**
 * The project's files (DC-01 to DC-04, slice S5).
 *
 * Six modules had been carrying a written note where a file belongs. This is the
 * register they attach to, and the two things it must be honest about are:
 *
 *   1. **Replacing a file keeps the old one.** Every version stays downloadable,
 *      by name, with who put it there and when. The screen says so before
 *      somebody replaces anything, because "replace" reads as "destroy" and
 *      here it is not.
 *   2. **Shared or not shared**, in words on every row. `is_external_visible`
 *      decides whether the client and the consultant can see a file, and a flag
 *      that important cannot live behind an edit dialog.
 *
 * The viewer (DC-04) shows PDFs and images INLINE — and only those. The
 * server honours `disposition=inline` for that short allow-list alone and
 * sends `Content-Security-Policy: sandbox` either way, so an HTML or SVG
 * file can never run its script on this origin. Everything else downloads.
 *
 * Folders (DC-01) organise the register, and carry the third fact worth
 * saying out loud: **sharing a folder shares every file in it** (`04` §6).
 *
 * ON THE REGISTER PATTERN — two things that did NOT move
 * -----------------------------------------------------
 * **The per-row folder select stays on the row.** It is the one control here
 * somebody uses repeatedly down the list: filing ten loose files is ten
 * selects, and behind a drawer it would be ten drawers. It is exactly the kind
 * of repeated act the pattern says to keep close.
 *
 * **The inline viewer stays a full-width panel**, not a drawer. A drawer is
 * 560px and a drawing is not readable in it. What opens in the drawer is the
 * record — its versions, and the form that adds one.
 */

const CATEGORY_LABELS: Readonly<Record<string, string>> = {
  drawing: 'Drawing',
  photo: 'Photo',
  certificate: 'Certificate',
  method_statement: 'Method statement',
  correspondence: 'Correspondence',
  contract: 'Contract',
  invoice: 'Invoice',
  report: 'Report',
  other: 'Other',
};

/** `1048576` → `1.0 MB`. Bytes are not a unit anybody reads. */
export function formatSize(bytes: string | number): string {
  const n = typeof bytes === 'string' ? Number(bytes) : bytes;
  if (!Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** How a file's visibility reads on the row, in words. */
function visibilityOf(document: DocumentRow): string {
  if (document.is_external_visible) return 'Shared with client';
  if (document.folder_external_visible) return 'Shared with client (via its folder)';
  return 'Internal';
}

interface Props {
  readonly projectId: string;
  readonly can: {
    readonly write: boolean;
    readonly edit: boolean;
    readonly remove: boolean;
    /** `document: X`. Gates the button; the rows are already filtered. */
    readonly exportRows: boolean;
  };
}

export function Documents({ projectId, can }: Props): JSX.Element {
  const [documents, setDocuments] = useState<readonly DocumentRow[] | null>(null);
  /**
   * How many files THIS reader may see on the server, against how many are
   * loaded. The server scopes the count too, so a client is never told there
   * are files they cannot open. See `api/paging.ts`.
   */
  const [registerTotal, setRegisterTotal] = useState(0);
  const [wantAll, setWantAll] = useState(false);
  /** Not the same as an empty register — see `Drawings.tsx`. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<DocumentDetail | null>(null);
  const [category, setCategory] = useState('drawing');
  const [shareOnUpload, setShareOnUpload] = useState(false);
  const [folders, setFolders] = useState<readonly Folder[]>([]);
  const [managingFolders, setManagingFolders] = useState(false);
  const [viewing, setViewing] = useState<DocumentRow | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const view = useRegisterView('added');

  /** '' = everything, 'none' = loose files, else a folder id. */
  const folderFilter = view.chosen.folder ?? '';

  const load = useCallback(async () => {
    try {
      const page = await listDocuments(
        projectId,
        undefined,
        undefined,
        wantAll ? LOAD_ALL_LIMIT : undefined,
      );
      setDocuments(page.rows);
      setRegisterTotal(page.total);
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the files.');
      setDocuments([]);
      return;
    }
    // Folders are allowed to fail alone — the file list is the register, the
    // tree is how it is arranged.
    try {
      setFolders(await listFolders(projectId));
    } catch {
      setFolders([]);
    }
  }, [projectId, wantAll]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * The rows on screen.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps` — which this repo treats as an
   * error. It runs before the early returns below because hooks must.
   */
  const shown = useMemo(() => {
    if (!documents) return [];

    const kind = view.chosen.kind;
    const visibility = view.chosen.visibility;

    const filtered = documents.filter((d) => {
      // Everything somebody would type: the file's name, where it is filed,
      // what kind of thing it is, and who put it there.
      const hay = [
        d.filename,
        d.folder_path,
        CATEGORY_LABELS[d.category] ?? d.category,
        d.uploaded_by_name,
      ]
        .filter(Boolean)
        .join(' ');
      if (!matches(hay, view.query)) return false;
      if (kind && d.category !== kind) return false;
      if (folderFilter === 'none' && d.folder_id !== null) return false;
      if (folderFilter && folderFilter !== 'none' && d.folder_id !== folderFilter) return false;
      // "Shared" counts a file shared BY ITS FOLDER too. Un-sharing the file
      // would not hide it, so a filter that ignored the folder would be lying.
      const isShared = d.is_external_visible || d.folder_external_visible;
      if (visibility === 'shared' && !isShared) return false;
      if (visibility === 'internal' && isShared) return false;
      return true;
    });

    const compare: Readonly<Record<string, (a: DocumentRow, b: DocumentRow) => number>> = {
      // Newest first: a file register is read from the most recent thing back.
      added: (a, b) => (a.created_at === b.created_at ? 0 : a.created_at < b.created_at ? 1 : -1),
      name: (a, b) => a.filename.localeCompare(b.filename),
      size: (a, b) => Number(b.size_bytes) - Number(a.size_bytes),
      versions: (a, b) => b.version_no - a.version_no,
    };

    return [...filtered].sort(compare[view.sortKey] ?? compare.added!);
  }, [documents, view.query, view.chosen.kind, view.chosen.visibility, folderFilter, view.sortKey]);

  /** Everything that is not the row's one visible action. */
  function overflowFor(document: DocumentRow, primary: RowAction): readonly RowAction[] {
    const actions: RowAction[] = [
      {
        label: 'Open',
        disabled: busy,
        onSelect: () => void run(async () => setOpen(await getDocument(projectId, document.id))),
      },
    ];
    if (can.edit) {
      actions.push({
        // The button says what will HAPPEN, not what is true now.
        label: document.is_external_visible ? 'Make internal' : 'Share',
        disabled: busy,
        onSelect: () =>
          void run(async () => {
            await updateDocument(projectId, document.id, {
              is_external_visible: !document.is_external_visible,
            });
            await load();
          }),
      });
      actions.push({
        label: 'Replace',
        disabled: busy,
        onSelect: () => void run(async () => setOpen(await getDocument(projectId, document.id))),
      });
    }
    if (can.remove) {
      actions.push({
        label: 'Remove',
        danger: true,
        disabled: busy,
        onSelect: () =>
          void run(async () => {
            await deleteDocument(projectId, document.id);
            await load();
          }),
      });
    }
    // Whatever is already on the row must not also be in the menu behind it.
    return actions.filter((action) => action.label !== primary.label);
  }

  async function exportRows(): Promise<void> {
    const headers = [
      'Name',
      'Kind',
      'Folder',
      'Size (bytes)',
      'Versions',
      'Visibility',
      'Added',
      'Added by',
    ];
    const rows = shown.map((d) => [
      d.filename,
      CATEGORY_LABELS[d.category] ?? d.category,
      d.folder_path ?? '',
      String(d.size_bytes),
      String(d.version_no),
      visibilityOf(d),
      d.created_at,
      d.uploaded_by_name ?? '',
    ]);
    setError(null);
    try {
      // Recorded BEFORE the file is written, and refusable — `09` §Threats
      // wants the row count and the filters. See `register-export.ts`.
      await exportRegister({
        projectId,
        resource: 'document',
        filename: `files-${projectId.slice(0, 8)}.csv`,
        headers,
        rows,
        filters: { query: view.query, ...view.chosen },
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not export.');
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Files</h3>
      {can.write && documents && (
        <>
          <label htmlFor="doc-file" className="link-button file-button">
            {busy ? 'Uploading…' : 'Add a file'}
          </label>
          <input
            id="doc-file"
            ref={fileInput}
            type="file"
            className="visually-hidden"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              void run(async () => {
                await uploadDocument(projectId, file, {
                  category,
                  external: shareOnUpload,
                  // Browsing a folder? New files land in it — that is what a
                  // person standing "inside" a folder expects.
                  ...(folderFilter && folderFilter !== 'none' ? { folderId: folderFilter } : {}),
                });
                await load();
              });
              // Cleared so choosing the SAME file twice still fires `change`.
              if (fileInput.current) fileInput.current.value = '';
            }}
          />
        </>
      )}
    </div>
  );

  if (denied) {
    return (
      <>
        {head}
        <p className="hint">Your role does not include this project&rsquo;s files.</p>
      </>
    );
  }

  if (!documents) {
    return (
      <>
        {head}
        <p aria-live="polite">Loading the files…</p>
      </>
    );
  }

  return (
    <>
      {head}

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {can.edit && folders.length >= 0 && (
        <div className="field-row">
          <div className="field">
            <button
              type="button"
              className="secondary inline"
              onClick={() => setManagingFolders((v) => !v)}
            >
              {managingFolders ? 'Close folders' : 'Manage folders'}
            </button>
          </div>
        </div>
      )}

      {managingFolders && can.edit && (
        <FoldersPanel
          projectId={projectId}
          folders={folders}
          busy={busy}
          canRemove={can.remove}
          onChanged={(next) => setFolders(next)}
          onError={(message) => setError(message)}
          setBusy={setBusy}
        />
      )}

      {can.write && (
        <div className="field-row">
          <div className="field">
            <label htmlFor="doc-category">What the next file is</label>
            <select
              id="doc-category"
              value={category}
              disabled={busy}
              onChange={(e) => setCategory(e.target.value)}
            >
              {DOCUMENT_CATEGORIES.map((key) => (
                <option key={key} value={key}>
                  {CATEGORY_LABELS[key] ?? key}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="doc-share">
              <input
                id="doc-share"
                type="checkbox"
                checked={shareOnUpload}
                disabled={busy}
                onChange={(e) => setShareOnUpload(e.target.checked)}
              />{' '}
              Share it with the client and consultant
            </label>
            <p className="hint">
              Files are internal unless you tick this. You can change it afterwards.
            </p>
          </div>
        </div>
      )}

      {documents.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No files yet.</strong>
          </p>
          <p>
            {can.write
              ? 'Drawings, photos, certificates and correspondence. Replacing a file keeps the old one.'
              : 'Nobody has added one, or none of them are yours to see.'}
          </p>
        </div>
      ) : (
        <>
          <RegisterToolbar
            view={view}
            searchLabel="Search files"
            searchPlaceholder="Name, folder, kind"
            shown={shown.length}
            total={documents.length}
            registerTotal={registerTotal}
            onLoadAll={() => setWantAll(true)}
            loadingAll={wantAll && documents.length < registerTotal}
            noun={['file', 'files']}
            filters={[
              {
                key: 'folder',
                label: 'Folder',
                options: [
                  { value: 'none', label: 'Not in a folder' },
                  ...folders.map((folder) => ({
                    value: folder.id,
                    label: `${folder.path}${folder.is_external_visible ? ' (shared)' : ''}`,
                  })),
                ],
              },
              {
                key: 'kind',
                label: 'Kind',
                options: DOCUMENT_CATEGORIES.map((key) => ({
                  value: key,
                  label: CATEGORY_LABELS[key] ?? key,
                })),
              },
              {
                key: 'visibility',
                label: 'Visibility',
                options: [
                  { value: 'shared', label: 'Shared with client' },
                  { value: 'internal', label: 'Internal' },
                ],
              },
            ]}
            sorts={[
              { key: 'added', label: 'Most recent' },
              { key: 'name', label: 'Name' },
              { key: 'size', label: 'Largest' },
              { key: 'versions', label: 'Most versions' },
            ]}
          >
            {can.exportRows && (
              <button type="button" className="secondary inline" onClick={() => void exportRows()}>
                Export {shown.length === documents.length ? 'all' : 'these'} to CSV
              </button>
            )}
          </RegisterToolbar>

          {shown.length === 0 ? (
            /*
             * A DIFFERENT empty state from "no files yet". This reader has a
             * register full of files and a filter that excludes all of them —
             * most often a folder they have just opened and not filled.
             */
            <div className="empty">
              <p>
                <strong>Nothing matches that.</strong>
              </p>
              <p>
                <button type="button" className="link-button" onClick={view.clear}>
                  Clear the search and filters
                </button>{' '}
                to see all {documents.length}.
              </p>
            </div>
          ) : (
            <div className={`rk-scroll${view.density === 'compact' ? ' rk-compact' : ''}`}>
              <table className="grid rk-cards">
                <caption className="visually-hidden">
                  Files on this project. {shown.length} of {documents.length} shown.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Kind</th>
                    <th scope="col">Folder</th>
                    <th scope="col">Size</th>
                    <th scope="col">Version</th>
                    {/*
                      "Visibility", not "Shared" — the column and one of its
                      values were the same word, so a screen reader announced
                      the cell as "Shared: Shared" and a reader scanning the
                      column could not tell the heading from an answer. Found by
                      the screen test.
                    */}
                    <th scope="col">Visibility</th>
                    <th scope="col">Added</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((document) => {
                    // A file the server will not serve gets NEITHER a download
                    // link nor the inline viewer — the viewer fetches the same
                    // bytes through the same route. The RECORD stays open: who
                    // uploaded it, when, and its version history are exactly
                    // what somebody needs in order to sort the file out.
                    const details: RowAction = {
                      label: 'Open',
                      disabled: busy,
                      onSelect: () =>
                        void run(async () => setOpen(await getDocument(projectId, document.id))),
                    };
                    const primary: RowAction =
                      document.can_download && isViewableInline(document.mime_type)
                        ? { label: 'View', onSelect: () => setViewing(document) }
                        : details;
                    return (
                      <tr key={document.id}>
                        <th scope="row">
                          {document.can_download ? (
                            <a href={documentFileUrl(projectId, document.id)} download>
                              {document.filename}
                            </a>
                          ) : (
                            <>
                              {document.filename}{' '}
                              {/*
                                In WORDS, never by colour alone — and it names
                                the state rather than only refusing. The two
                                cases send a reader to two different people: a
                                virus is the IT team's problem, an unchecked
                                file is the administrator's.
                              */}
                              <span className="hint">
                                {document.scan_status === 'infected'
                                  ? '— malware found, cannot be downloaded'
                                  : '— not checked for malware yet, cannot be downloaded'}
                              </span>
                            </>
                          )}
                        </th>
                        <td data-label="Kind">
                          {CATEGORY_LABELS[document.category] ?? document.category}
                        </td>
                        <td data-label="Folder">
                          {can.edit ? (
                            <>
                              <label
                                className="visually-hidden"
                                htmlFor={`doc-folder-${document.id}`}
                              >
                                Folder for {document.filename}
                              </label>
                              <select
                                id={`doc-folder-${document.id}`}
                                value={document.folder_id ?? ''}
                                disabled={busy}
                                onChange={(e) =>
                                  void run(async () => {
                                    await moveDocumentToFolder(
                                      projectId,
                                      document.id,
                                      e.target.value || null,
                                    );
                                    await load();
                                  })
                                }
                              >
                                <option value="">—</option>
                                {folders.map((folder) => (
                                  <option key={folder.id} value={folder.id}>
                                    {folder.path}
                                  </option>
                                ))}
                              </select>
                            </>
                          ) : (
                            (document.folder_path ?? '—')
                          )}
                        </td>
                        <td data-label="Size">{formatSize(document.size_bytes)}</td>
                        <td data-label="Version">
                          {document.version_no > 1 ? (
                            <button
                              type="button"
                              className="link-button"
                              onClick={() =>
                                void run(async () =>
                                  setOpen(await getDocument(projectId, document.id)),
                                )
                              }
                            >
                              {document.version_no} versions
                              <span className="visually-hidden"> of {document.filename}</span>
                            </button>
                          ) : (
                            <span className="hint">1</span>
                          )}
                        </td>
                        {/* Words, not a colour or a tick. Who can see a file is
                            the single most consequential fact on this row, and
                            "Shared" alone does not say who with. A file shared
                            BY ITS FOLDER says that too — un-sharing the file
                            would not hide it. */}
                        <td data-label="Visibility">{visibilityOf(document)}</td>
                        <td data-label="Added">
                          {formatDate(document.created_at)}
                          {document.uploaded_by_name && (
                            <span className="hint"> · {document.uploaded_by_name}</span>
                          )}
                        </td>
                        <td>
                          <RowActions
                            rowLabel={document.filename}
                            primary={primary}
                            more={overflowFor(document, primary)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {open && (
        <Drawer
          title={open.filename}
          subtitle={`${open.version_no} ${open.version_no === 1 ? 'version' : 'versions'}`}
          onClose={() => setOpen(null)}
        >
          <VersionPanel
            projectId={projectId}
            document={open}
            canReplace={can.edit}
            busy={busy}
            onClose={() => setOpen(null)}
            onReplaced={async (file, note) => {
              await run(async () => {
                setOpen(await addDocumentVersion(projectId, open.id, file, note));
                await load();
              });
            }}
          />
        </Drawer>
      )}

      {/*
        Full width, not a drawer. A drawer is 560px and a drawing is not
        readable in one — this is the only place in the product where the
        content needs the whole page more than the register needs to stay put.
      */}
      {viewing && (
        <div className="panel">
          <p className="panel-title">{viewing.filename}</p>
          {viewing.mime_type.startsWith('image/') ? (
            <img
              src={documentFileUrl(projectId, viewing.id, undefined, 'inline')}
              alt={viewing.filename}
              style={{ maxWidth: '100%' }}
            />
          ) : (
            <iframe
              src={documentFileUrl(projectId, viewing.id, undefined, 'inline')}
              title={viewing.filename}
              style={{ width: '100%', height: '70vh', border: 0 }}
            />
          )}
          <div className="button-row">
            <a href={documentFileUrl(projectId, viewing.id)} download className="link-button">
              Download {viewing.filename}
            </a>
            <button type="button" className="secondary inline" onClick={() => setViewing(null)}>
              Close the viewer
            </button>
          </div>
        </div>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------

interface FoldersPanelProps {
  readonly projectId: string;
  readonly folders: readonly Folder[];
  readonly busy: boolean;
  readonly canRemove: boolean;
  readonly onChanged: (folders: readonly Folder[]) => void;
  readonly onError: (message: string) => void;
  readonly setBusy: (busy: boolean) => void;
}

/**
 * Create, rename, share and remove folders. The one sentence that matters is
 * written where the share toggle is: sharing a folder shares every file in it.
 */
function FoldersPanel({
  projectId,
  folders,
  busy,
  canRemove,
  onChanged,
  onError,
  setBusy,
}: FoldersPanelProps): JSX.Element {
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameTo, setRenameTo] = useState('');

  async function run(work: () => Promise<readonly Folder[]>): Promise<void> {
    setBusy(true);
    onError('');
    try {
      onChanged(await work());
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <p className="panel-title">Folders</p>
      <p className="hint">
        Sharing a folder shares every file in it with the client and consultant — including
        files added to it later.
      </p>

      {folders.length > 0 && (
        <ul>
          {folders.map((folder) => (
            <li key={folder.id}>
              {renaming === folder.id ? (
                <>
                  <label className="visually-hidden" htmlFor={`folder-rename-${folder.id}`}>
                    New name for {folder.name}
                  </label>
                  <input
                    id={`folder-rename-${folder.id}`}
                    value={renameTo}
                    disabled={busy}
                    onChange={(e) => setRenameTo(e.target.value)}
                    maxLength={120}
                  />
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy || !renameTo.trim()}
                    onClick={() =>
                      void run(async () => {
                        const next = await updateFolder(projectId, folder.id, {
                          name: renameTo.trim(),
                        });
                        setRenaming(null);
                        return next;
                      })
                    }
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    onClick={() => setRenaming(null)}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span>{folder.path}</span>{' '}
                  <span className="hint">
                    ({folder.document_count} file{folder.document_count === 1 ? '' : 's'} ·{' '}
                    {folder.is_external_visible ? 'shared with client' : 'internal'})
                  </span>{' '}
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    onClick={() =>
                      void run(() =>
                        updateFolder(projectId, folder.id, {
                          is_external_visible: !folder.is_external_visible,
                        }),
                      )
                    }
                  >
                    {folder.is_external_visible ? 'Make internal' : 'Share folder'}
                    <span className="visually-hidden"> — {folder.path}</span>
                  </button>{' '}
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    onClick={() => {
                      setRenaming(folder.id);
                      setRenameTo(folder.name);
                    }}
                  >
                    Rename<span className="visually-hidden"> {folder.path}</span>
                  </button>
                  {canRemove && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="link-button danger"
                        disabled={busy}
                        onClick={() => void run(() => deleteFolder(projectId, folder.id))}
                      >
                        Remove<span className="visually-hidden"> {folder.path}</span>
                      </button>
                    </>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="field-row">
        <div className="field">
          <label htmlFor="folder-name">New folder</label>
          <input
            id="folder-name"
            value={name}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            placeholder="Contracts"
          />
        </div>
        <div className="field">
          <label htmlFor="folder-parent">Inside</label>
          <select
            id="folder-parent"
            value={parentId}
            disabled={busy}
            onChange={(e) => setParentId(e.target.value)}
          >
            <option value="">Top level</option>
            {folders.map((folder) => (
              <option key={folder.id} value={folder.id}>
                {folder.path}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <button
            type="button"
            className="inline"
            disabled={busy || !name.trim()}
            onClick={() =>
              void run(async () => {
                const next = await createFolder(projectId, {
                  name: name.trim(),
                  parent_id: parentId || null,
                });
                setName('');
                return next;
              })
            }
          >
            Create folder
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function VersionPanel({
  projectId,
  document: doc,
  canReplace,
  busy,
  onClose,
  onReplaced,
}: {
  readonly projectId: string;
  readonly document: DocumentDetail;
  readonly canReplace: boolean;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onReplaced: (file: File, note: string) => void | Promise<void>;
}): JSX.Element {
  const [note, setNote] = useState('');
  const input = useRef<HTMLInputElement>(null);

  return (
    // The drawer's own heading already names the file, so the panel does not
    // name it a second time.
    <div className="panel">
      <p className="hint">
        Every version is kept. Replacing this file does not delete the one that is here now —
        it stays downloadable, with who put it there and when.
      </p>

      <table className="grid">
        <caption className="visually-hidden">Versions of {doc.filename}</caption>
        <thead>
          <tr>
            <th scope="col">Version</th>
            <th scope="col">Name</th>
            <th scope="col">Size</th>
            <th scope="col">Why</th>
            <th scope="col">Added</th>
          </tr>
        </thead>
        <tbody>
          {doc.versions.map((version) => (
            <tr key={version.id}>
              <th scope="row">
                <a href={documentFileUrl(projectId, doc.id, version.version_no)} download>
                  {version.version_no}
                  {version.version_no === doc.version_no && (
                    <span className="hint"> (current)</span>
                  )}
                </a>
              </th>
              <td data-label="Name">{version.filename}</td>
              <td data-label="Size">{formatSize(version.size_bytes)}</td>
              <td data-label="Why">{version.note ?? '—'}</td>
              <td data-label="Added">
                {formatDate(version.uploaded_at)}
                {version.uploaded_by_name && (
                  <span className="hint"> · {version.uploaded_by_name}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {canReplace && (
        <>
          <div className="field">
            <label htmlFor="doc-note">Why is it being replaced?</label>
            <input
              id="doc-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              disabled={busy}
              placeholder="Client moved the reception desk."
            />
            <p className="hint">
              Optional, and the one thing that makes this list readable in six months.
            </p>
          </div>

          <div className="button-row">
            <label htmlFor="doc-replace" className="link-button file-button">
              {busy ? 'Uploading…' : 'Choose the new file'}
            </label>
            <input
              id="doc-replace"
              ref={input}
              type="file"
              className="visually-hidden"
              disabled={busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                void onReplaced(file, note.trim());
                setNote('');
                if (input.current) input.current.value = '';
              }}
            />
            <button type="button" className="secondary inline" onClick={onClose} disabled={busy}>
              Close
            </button>
          </div>
        </>
      )}

      {!canReplace && (
        <div className="button-row">
          <button type="button" className="secondary inline" onClick={onClose}>
            Close
          </button>
        </div>
      )}
    </div>
  );
}
